import React from "react";
import WebProfitSpinner from "../../../system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { productsApi } from "../../../../api/products";
import { formatApiError } from "../../../../api/client";
import ImportSourceStep from "./ImportSourceStep";
import ImportSampleStep from "./ImportSampleStep";
import ImportMappingStep from "./ImportMappingStep";
import ImportDefaultsStep from "./ImportDefaultsStep";
import ImportSupplierSelectionStep from "./ImportSupplierSelectionStep";
import ImportPreviewStep from "./ImportPreviewStep";
import ImportResultStep from "./ImportResultStep";
import { PriceImportMappingStep, PriceImportPreviewStep, PriceImportResultStep, PriceImportSourceExtras } from "./ProductPriceImportSteps";
import { deriveImportColumnSamples, mappedTarget, suggestImportMapping } from "./importFields";
import { reportActiveDashboardTutorialMetric } from "../../../../utils/dashboardTutorial";

const PRICE_STEPS = ["Archivo", "Código y precio", "Revisión", "Confirmación"];
const GENERAL_STEPS = ["Fuente", "Muestra", "Mapping", "Defaults y modo", "Preview", "Confirmación"];
const SUPPLIER_STEPS = ["Fuente", "Muestra", "Mapping", "Defaults", "Selección", "Preview", "Confirmación"];
const GENERAL_DEFAULTS = {
  category: "General",
  unit: "",
  itemKind: "product",
  active: true,
  publicVisible: true,
  sellable: true,
  purchasable: true,
};
const SUPPLIER_DEFAULTS = { ...GENERAL_DEFAULTS, publicVisible: false };
const REQUIRED_DESTINATION_FIELDS = [
  "name", "category", "price", "stock", "code", "unit", "itemKind", "active", "publicVisible", "sellable", "purchasable",
];
const REQUIRED_MODES = ["create_only", "upsert_by_code"];

function contractError(message) {
  const error = new Error(message);
  error.code = "PRODUCT_IMPORT_CONTRACT_INVALID";
  return error;
}

function assertDraftContract(response, context) {
  const importId = String(response?.importId || "").trim();
  const headers = Array.isArray(response?.headers) ? response.headers.map(String) : [];
  const rowCount = Number(response?.rowCount);
  const destinationFields = response?.contract?.destinationFields;
  const modes = response?.contract?.modes;

  if (!importId) throw contractError("El backend no devolvió un importId válido para el borrador.");
  if (!headers.length || !Number.isFinite(rowCount) || rowCount < 1) throw contractError("El backend devolvió una muestra de importación incompleta.");
  if (!destinationFields || typeof destinationFields !== "object" || Array.isArray(destinationFields)) throw contractError("El backend no devolvió el contrato de campos del importador.");
  const requiredFields = context === "prices" ? ["code", "price"] : context === "supplier" ? [...REQUIRED_DESTINATION_FIELDS, "supplierCost"] : REQUIRED_DESTINATION_FIELDS;
  const missingFields = requiredFields.filter((field) => !destinationFields[field]);
  if (missingFields.length) throw contractError(`El contrato del importador no soporta: ${missingFields.join(", ")}.`);
  if (!Array.isArray(modes) || REQUIRED_MODES.some((mode) => !modes.includes(mode))) throw contractError("El contrato del importador no soporta los modos requeridos por el panel.");
  if (context === "supplier" && (!modes.includes("supplier_proposal") || !response?.contract?.contexts?.includes("supplier"))) {
    throw contractError("El backend no soporta el flujo seguro de lista de proveedor.");
  }
  if (context === "prices" && (response.purpose !== "prices" || !modes.includes("update_prices") || !response.contract.contexts?.includes("prices") || response.contract.priceImport?.createsAllowed !== false)) {
    throw contractError("El backend todavía no soporta la actualización exclusiva de precios. Actualizá también el backend.");
  }
  return { importId, headers, rowCount, contract: response.contract };
}

function assertPreviewContract(response, importId, context = "general") {
  const responseImportId = String(response?.importId || "").trim();
  if (!responseImportId || responseImportId !== String(importId || "").trim()) throw contractError("La vista previa no corresponde al borrador de importación activo.");
  const totals = response?.totals;
  const totalKeys = ["rows", "created", "updated", "skipped", "errors"];
  if (!totals || typeof totals !== "object" || totalKeys.some((key) => !Number.isFinite(Number(totals[key])) || Number(totals[key]) < 0)) {
    throw contractError("El backend devolvió totales inválidos en la vista previa.");
  }
  if (!Array.isArray(response?.rows) || !Array.isArray(response?.errors)) throw contractError("El backend devolvió una vista previa con filas o errores en un formato inválido.");
  if (response?.stockPolicy?.directWrite !== false) throw contractError("La política de stock del preview no garantiza conciliación mediante movimientos ERP.");
  if (typeof response?.canCommit !== "boolean" || !Array.isArray(response?.blockingErrors)) throw contractError("El backend no informó si la preview está habilitada para commit.");
  if (response?.context === "supplier") {
    if (!response?.supplier || response.supplier.allRowsReturned !== true || response.rows.length !== Number(totals.rows)) {
      throw contractError("La propuesta de proveedor no representa todas las filas del archivo.");
    }
  }
  if (context === "prices" && (response.context !== "prices" || response.pricesOnly !== true || response.allRowsReturned !== true || response.rows.length !== Number(totals.rows) || Number(totals.created) !== 0 || response.stockPolicy.mapped !== false)) {
    throw contractError("La vista previa no garantiza una actualización exclusiva de precios.");
  }
  return true;
}

function assertCommitContract(response, importId, context = "general") {
  const responseImportId = String(response?.importId || "").trim();
  if (!responseImportId || responseImportId !== String(importId || "").trim()) throw contractError("El resultado del commit no corresponde al borrador activo.");
  if (!["completed", "failed"].includes(String(response?.status || ""))) throw contractError("El backend devolvió un estado final de importación inválido.");
  const totals = response?.totals;
  if (!totals || ["rows", "created", "updated", "skipped", "errors", "stockMovements"].some((key) => !Number.isFinite(Number(totals[key])) || Number(totals[key]) < 0)) {
    throw contractError("El backend devolvió un resumen final inválido.");
  }
  if (!Array.isArray(response?.errors) || !response?.stock || !Array.isArray(response.stock.movements)) throw contractError("El backend devolvió un resultado final incompleto.");
  if (context === "prices" && (response.context !== "prices" || response.pricesOnly !== true || !Array.isArray(response.rows) || response.rows.length !== Number(totals.rows) || Number(totals.created) !== 0 || Number(totals.stockMovements) !== 0)) {
    throw contractError("El resultado no corresponde a una actualización exclusiva de precios.");
  }
  return true;
}

export default function ProductImportWizard({
  open,
  projectId,
  context = "general",
  canManageCosts = false,
  onClose,
  onImported,
}) {
  const supplierMode = context === "supplier";
  const priceMode = context === "prices";
  const steps = priceMode ? PRICE_STEPS : supplierMode ? SUPPLIER_STEPS : GENERAL_STEPS;
  const fullScreen = useMediaQuery("(max-width:699.95px)", { noSsr: true });
  const [allowZero, setAllowZero] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const [sourceMode, setSourceMode] = React.useState("file");
  const [file, setFile] = React.useState(null);
  const [googleUrl, setGoogleUrl] = React.useState("");
  const [draft, setDraft] = React.useState(null);
  const [contract, setContract] = React.useState(null);
  const [mapping, setMapping] = React.useState({});
  const [automaticSuggestions, setAutomaticSuggestions] = React.useState({});
  const [defaults, setDefaults] = React.useState(priceMode ? {} : supplierMode ? SUPPLIER_DEFAULTS : GENERAL_DEFAULTS);
  const [mode, setMode] = React.useState(priceMode ? "update_prices" : supplierMode ? "supplier_proposal" : "create_only");
  const [supplierProposal, setSupplierProposal] = React.useState(null);
  const [supplierActions, setSupplierActions] = React.useState({});
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const importIdRef = React.useRef("");
  const draftProjectIdRef = React.useRef("");
  const committingRef = React.useRef(false);
  // BLOCK 1 · passive analytics only. A telemetry failure must never block an import.
  // STATUS: STATIC_PASS · runtime import scenarios pending staging.
  const telemetryStartedRef = React.useRef(false);
  const telemetryFinalizedRef = React.useRef(false);
  const telemetryOperationIdRef = React.useRef("");
  const telemetryAttemptRef = React.useRef(0);

  const resetState = React.useCallback(() => {
    setStep(0);
    setSourceMode("file");
    setFile(null);
    setGoogleUrl("");
    setDraft(null);
    setContract(null);
    setMapping({});
    setAutomaticSuggestions({});
    setAllowZero(false);
    setDefaults(priceMode ? {} : supplierMode ? SUPPLIER_DEFAULTS : GENERAL_DEFAULTS);
    setMode(priceMode ? "update_prices" : supplierMode ? "supplier_proposal" : "create_only");
    setSupplierProposal(null);
    setSupplierActions({});
    setPreview(null);
    setResult(null);
    committingRef.current = false;
    telemetryStartedRef.current = false;
    telemetryFinalizedRef.current = false;
    telemetryOperationIdRef.current = "";
    setBusy(false);
    setError("");
  }, [priceMode, supplierMode]);

  const discardDraft = React.useCallback(async () => {
    const importId = importIdRef.current;
    const draftProjectId = draftProjectIdRef.current;
    importIdRef.current = "";
    draftProjectIdRef.current = "";
    if (!importId || !draftProjectId) return;
    try { await productsApi.deleteImport(draftProjectId, importId, context); } catch { /* TTL de backend */ }
  }, [context]);

  React.useEffect(() => () => {
    const importId = importIdRef.current;
    const draftProjectId = draftProjectIdRef.current;
    if (importId && draftProjectId) void productsApi.deleteImport(draftProjectId, importId, context).catch(() => {});
  }, [context]);

  const handleClose = React.useCallback(() => {
    if (busy) return;
    if (telemetryStartedRef.current && !telemetryFinalizedRef.current) {
      reportActiveDashboardTutorialMetric("import_abandoned", {
        operationId: telemetryOperationIdRef.current,
        attemptNumber: telemetryAttemptRef.current,
        sourceType: sourceMode === "url" ? "google_sheets" : "file",
        itemCount: Number(draft?.rowCount || 0),
        processedCount: 0,
        errorCount: Number(preview?.totals?.errors || 0),
      });
      telemetryFinalizedRef.current = true;
    }
    void discardDraft();
    resetState();
    onClose?.();
  }, [busy, discardDraft, draft?.rowCount, onClose, preview?.totals?.errors, resetState, sourceMode]);

  const createDraft = async () => {
    setBusy(true);
    setError("");
    telemetryStartedRef.current = true;
    telemetryFinalizedRef.current = false;
    telemetryAttemptRef.current += 1;
    telemetryOperationIdRef.current = `import:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`;
    reportActiveDashboardTutorialMetric("import_started", {
      operationId: telemetryOperationIdRef.current,
      attemptNumber: telemetryAttemptRef.current,
      sourceType: sourceMode === "url" ? "google_sheets" : "file",
      itemCount: 0,
      processedCount: 0,
      errorCount: 0,
    });
    try {
      await discardDraft();
      const response = await productsApi.createImport(projectId, sourceMode === "file" ? { file, context } : { url: googleUrl, context });
      const responseImportId = String(response?.importId || "").trim();
      if (responseImportId) {
        importIdRef.current = responseImportId;
        draftProjectIdRef.current = projectId;
      }
      const validated = assertDraftContract(response, context);
      const columnSamples = deriveImportColumnSamples({ headers: validated.headers, columnSamples: response?.columnSamples, sampleRows: response?.sampleRows });
      const suggestedMapping = suggestImportMapping(validated.headers, columnSamples, { context });
      if (supplierMode && !canManageCosts) {
        for (const [source, target] of Object.entries(suggestedMapping)) {
          if (target === "supplierCost") suggestedMapping[source] = "ignore";
        }
      }
      setDraft(response);
      setContract(validated.contract);
      setFile(null);
      setMapping(suggestedMapping);
      setAutomaticSuggestions(Object.fromEntries(Object.entries(suggestedMapping).filter(([, target]) => target && target !== "ignore")));
      setSupplierProposal(null);
      setSupplierActions({});
      setPreview(null);
      setResult(null);
      setStep(1);
    } catch (err) {
      if (String(err?.code || "") === "PRODUCT_IMPORT_CONTRACT_INVALID") await discardDraft();
      reportActiveDashboardTutorialMetric("import_failed", {
        operationId: telemetryOperationIdRef.current,
        attemptNumber: telemetryAttemptRef.current,
        sourceType: sourceMode === "url" ? "google_sheets" : "file",
        itemCount: 0,
        processedCount: 0,
        errorCount: 1,
      });
      telemetryFinalizedRef.current = true;
      setError(formatApiError(err, "No se pudo preparar la importación"));
    } finally { setBusy(false); }
  };

  const mappingHasName = Boolean(mappedTarget(mapping, "name"));
  const codeMapped = Boolean(mappedTarget(mapping, "code"));
  const mappingReady = priceMode ? codeMapped && Boolean(mappedTarget(mapping, "price")) : mappingHasName;
  const columnSamples = React.useMemo(() => deriveImportColumnSamples({
    headers: draft?.headers || [], columnSamples: draft?.columnSamples, sampleRows: draft?.sampleRows || [],
  }), [draft]);

  React.useEffect(() => {
    if (!supplierMode && !codeMapped && mode === "upsert_by_code") setMode("create_only");
  }, [codeMapped, mode, supplierMode]);

  const invalidatePreview = React.useCallback(() => {
    setSupplierProposal(null);
    setSupplierActions({});
    setPreview(null);
    setResult(null);
  }, []);

  const reinterpretDraft = async (headerRowIndex) => {
    if (busy || !draft?.importId || headerRowIndex === Number(draft?.headerRowIndex || 0)) return;
    setBusy(true);
    setError("");
    try {
      const response = await productsApi.reinterpretImport(projectId, draft.importId, { headerRowIndex, context });
      const validated = assertDraftContract(response, context);
      if (validated.importId !== String(draft.importId)) {
        throw contractError("La reinterpretación no corresponde al borrador de importación activo.");
      }
      const nextColumnSamples = deriveImportColumnSamples({
        headers: validated.headers,
        columnSamples: response?.columnSamples,
        sampleRows: response?.sampleRows,
      });
      const suggestedMapping = suggestImportMapping(validated.headers, nextColumnSamples, { context });
      if (supplierMode && !canManageCosts) {
        for (const [source, target] of Object.entries(suggestedMapping)) {
          if (target === "supplierCost") suggestedMapping[source] = "ignore";
        }
      }
      setDraft(response);
      setContract(validated.contract);
      setMapping(suggestedMapping);
      setAutomaticSuggestions(Object.fromEntries(
        Object.entries(suggestedMapping).filter(([, target]) => target && target !== "ignore"),
      ));
      invalidatePreview();
    } catch (err) {
      setError(formatApiError(err, "No se pudo reinterpretar la planilla"));
    } finally {
      setBusy(false);
    }
  };

  const updateMapping = React.useCallback((source, target) => {
    setMapping((current) => ({ ...current, [source]: target }));
    setAutomaticSuggestions((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, source)) return current;
      const next = { ...current }; delete next[source]; return next;
    });
    invalidatePreview();
  }, [invalidatePreview]);

  const updateDefaults = React.useCallback((field, value) => {
    setDefaults((current) => ({ ...current, [field]: value }));
    invalidatePreview();
  }, [invalidatePreview]);

  const updateMode = React.useCallback((value) => {
    setMode(value); invalidatePreview();
  }, [invalidatePreview]);

  const requestPreview = async (rowActions = undefined) => {
    if (!draft?.importId || !mappingReady) return null;
    const response = await productsApi.previewImport(projectId, draft.importId, {
      mapping,
      defaults: priceMode ? {} : defaults,
      mode: priceMode ? "update_prices" : supplierMode ? "supplier_proposal" : mode,
      context,
      ...(priceMode ? { allowZero } : {}),
      ...(supplierMode ? { rowActions: rowActions || {} } : {}),
    });
    assertPreviewContract(response, draft.importId, context);
    return response;
  };

  const generatePreview = async () => {
    setBusy(true); setError(""); setPreview(null);
    try {
      const response = await requestPreview();
      if (!response) return;
      setPreview(response); setResult(null); setStep(priceMode ? 2 : 4);
    } catch (err) { setError(formatApiError(err, "No se pudo generar la vista previa")); }
    finally { setBusy(false); }
  };

  const generateSupplierProposal = async () => {
    setBusy(true); setError(""); setSupplierProposal(null); setPreview(null);
    try {
      const response = await requestPreview({});
      if (!response) return;
      setSupplierProposal(response); setSupplierActions({}); setResult(null); setStep(4);
    } catch (err) { setError(formatApiError(err, "No se pudo analizar la lista del proveedor")); }
    finally { setBusy(false); }
  };

  const finalizeSupplierSelection = async () => {
    if (!Object.keys(supplierActions).length) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const response = await requestPreview(supplierActions);
      if (!response) return;
      setPreview(response); setResult(null); setStep(5);
    } catch (err) { setError(formatApiError(err, "No se pudo validar la selección")); }
    finally { setBusy(false); }
  };

  const previewIsCurrent = React.useMemo(() => {
    if (!preview || !draft?.importId) return false;
    try { return assertPreviewContract(preview, draft.importId, context); } catch { return false; }
  }, [context, draft?.importId, preview]);
  const previewCanCommit = previewIsCurrent && preview?.canCommit === true;

  const commitImport = async () => {
    if (!draft?.importId || !previewCanCommit || committingRef.current) return;
    committingRef.current = true; setBusy(true); setError("");
    try {
      const response = await productsApi.commitImport(projectId, draft.importId, context);
      assertCommitContract(response, draft.importId, context);
      const totals = response?.totals || {};
      reportActiveDashboardTutorialMetric(response?.status === "completed" ? "import_completed" : "import_failed", {
        operationId: telemetryOperationIdRef.current,
        attemptNumber: telemetryAttemptRef.current,
        sourceType: sourceMode === "url" ? "google_sheets" : "file",
        itemCount: Number(totals.rows || draft?.rowCount || 0),
        processedCount: Math.max(0, Number(totals.rows || 0) - Number(totals.errors || 0)),
        errorCount: Number(totals.errors || 0),
      });
      telemetryFinalizedRef.current = true;
      setResult(response); await onImported?.();
    } catch (err) {
      const failedResult = err?.data?.status === "failed" ? err.data : null;
      if (failedResult) {
        try {
          assertCommitContract(failedResult, draft.importId, context);
          const totals = failedResult?.totals || {};
          reportActiveDashboardTutorialMetric("import_failed", {
            operationId: telemetryOperationIdRef.current,
            attemptNumber: telemetryAttemptRef.current,
            sourceType: sourceMode === "url" ? "google_sheets" : "file",
            itemCount: Number(totals.rows || draft?.rowCount || 0),
            processedCount: Math.max(0, Number(totals.rows || 0) - Number(totals.errors || 0)),
            errorCount: Number(totals.errors || 1),
          });
          telemetryFinalizedRef.current = true;
          setResult(failedResult);
          await onImported?.();
        } catch (contractErr) {
          reportActiveDashboardTutorialMetric("import_failed", { operationId: telemetryOperationIdRef.current, attemptNumber: telemetryAttemptRef.current, sourceType: sourceMode === "url" ? "google_sheets" : "file", errorCount: 1 });
          telemetryFinalizedRef.current = true;
          setError(formatApiError(contractErr, "El commit devolvió un resultado inválido"));
        }
      } else {
        reportActiveDashboardTutorialMetric("import_failed", { operationId: telemetryOperationIdRef.current, attemptNumber: telemetryAttemptRef.current, sourceType: sourceMode === "url" ? "google_sheets" : "file", itemCount: Number(draft?.rowCount || 0), errorCount: 1 });
        telemetryFinalizedRef.current = true;
        setError(formatApiError(err, "No se pudo confirmar la importación"));
      }
    } finally { committingRef.current = false; setBusy(false); }
  };

  const openPriceHistory = async (importId) => {
    setBusy(true); setError("");
    try {
      const response = await productsApi.priceImportResult(projectId, importId);
      assertCommitContract(response, importId, context);
      await discardDraft();
      setDraft({ importId, rowCount: response.totals.rows, source: response.source });
      setPreview(null); setResult(response); setStep(3);
    } catch (requestError) { setError(formatApiError(requestError, "No se pudo abrir el lote de precios.")); }
    finally { setBusy(false); }
  };

  const goBack = () => { setError(""); setStep((current) => Math.max(0, current - 1)); };
  const goForward = async () => {
    setError("");
    if (priceMode) {
      if (step === 1 && mappingReady) await generatePreview();
      else if (step === 2 && previewCanCommit) setStep(3);
      return;
    }
    if (step === 1) setStep(2);
    else if (step === 2 && mappingHasName) setStep(3);
    else if (step === 3) {
      if (supplierMode) await generateSupplierProposal(); else await generatePreview();
    } else if (supplierMode && step === 4) await finalizeSupplierSelection();
    else if (supplierMode && step === 5 && previewCanCommit) setStep(6);
    else if (!supplierMode && step === 4 && previewCanCommit) setStep(5);
  };

  const renderStep = () => {
    if (priceMode) {
      if (step === 0) return <Stack spacing={3}><PriceImportSourceExtras projectId={projectId} busy={busy} onOpenHistory={openPriceHistory} /><ImportSourceStep sourceMode={sourceMode} onSourceModeChange={setSourceMode} file={file} onFileChange={setFile} googleUrl={googleUrl} onGoogleUrlChange={setGoogleUrl} busy={busy} onContinue={createDraft} maxColumns={512} /></Stack>;
      if (step === 1) return <Stack spacing={3}><PriceImportMappingStep headers={draft?.headers} mapping={mapping} columnSamples={columnSamples} allowZero={allowZero} onAllowZeroChange={(value) => { setAllowZero(value); invalidatePreview(); }} onChange={(value) => { setMapping(value); invalidatePreview(); }} /><ImportSampleStep draft={draft} busy={busy} onHeaderRowChange={reinterpretDraft} compact /></Stack>;
      if (step === 2) return <PriceImportPreviewStep preview={preview} />;
      return <PriceImportResultStep preview={preview} result={result} busy={busy} onCommit={commitImport} />;
    }
    if (step === 0) return <ImportSourceStep sourceMode={sourceMode} onSourceModeChange={setSourceMode} file={file} onFileChange={setFile} googleUrl={googleUrl} onGoogleUrlChange={setGoogleUrl} busy={busy} onContinue={createDraft} />;
    if (step === 1) return <ImportSampleStep draft={draft} busy={busy} onHeaderRowChange={reinterpretDraft} />;
    if (step === 2) return (
      <ImportMappingStep headers={draft?.headers || []} mapping={mapping} automaticSuggestions={automaticSuggestions} columnSamples={columnSamples} contract={contract} context={context} canManageCosts={canManageCosts} onChange={updateMapping} />
    );
    if (step === 3) return (
      <ImportDefaultsStep defaults={defaults} onDefaultsChange={updateDefaults} mode={mode} onModeChange={updateMode} codeMapped={codeMapped} context={context} />
    );
    if (supplierMode && step === 4) return (
      <ImportSupplierSelectionStep rows={supplierProposal?.rows || []} rowActions={supplierActions} onActionsChange={(actions) => { setSupplierActions(actions); setPreview(null); setResult(null); }} />
    );
    if ((supplierMode && step === 5) || (!supplierMode && step === 4)) return <ImportPreviewStep preview={preview} />;
    return <ImportResultStep preview={preview} result={result} busy={busy} onCommit={commitImport} />;
  };

  const finalStep = priceMode ? 3 : supplierMode ? 6 : 5;
  const showFooterNext = step > 0 && step < finalStep;
  const supplierSelectedCount = Object.keys(supplierActions).length;
  const footerNextDisabled = busy
    || (priceMode && step === 1 && !mappingReady)
    || (priceMode && step === 2 && !previewCanCommit)
    || (!priceMode && step === 2 && !mappingHasName)
    || (supplierMode && step === 4 && supplierSelectedCount < 1)
    || (supplierMode && step === 5 && !previewCanCommit)
    || (!supplierMode && step === 4 && !previewCanCommit);

  const nextLabel = priceMode ? (step === 1 ? "Revisar precios" : "Continuar a confirmación") : step === 3
    ? (supplierMode ? "Analizar productos" : "Generar preview")
    : supplierMode && step === 4
      ? `Revisar ${supplierSelectedCount || 0} seleccionado(s)`
      : ((supplierMode && step === 5) || (!supplierMode && step === 4))
        ? "Revisar confirmación"
        : "Continuar";

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={fullScreen} fullWidth maxWidth="lg" PaperProps={{ sx: { minHeight: fullScreen ? "100dvh" : "min(820px, calc(100dvh - 48px))" } }}>
      <DialogTitle sx={{ pr: 7, pb: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>{priceMode ? "PRECIOS" : supplierMode ? "LISTA DE PROVEEDOR" : "IMPORTADOR DE ÍTEMS"}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 950 }}>{priceMode ? "Actualizar precios desde archivo" : supplierMode ? "Cargar lista de proveedor" : "Importar ítems"}</Typography>
        <IconButton aria-label="Cerrar importador" onClick={handleClose} disabled={busy} sx={{ position: "absolute", right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      {busy ? <WebProfitSpinner size={22} sx={{ mx: "auto", my: 0.75 }} /> : null}
      <DialogContent dividers sx={{ bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", p: { xs: 1.5, sm: 2.5 } }}>
        <Stack spacing={2.5}>
          <Box sx={{ overflowX: "auto", pb: 0.5 }}>
            <Stepper activeStep={step} sx={{ minWidth: priceMode ? 520 : supplierMode ? 820 : 720 }}>
              {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>
          </Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {busy && !error ? <Stack direction="row" spacing={1} alignItems="center"><WebProfitSpinner size={18} /><Typography variant="body2" color="text.secondary">Procesando…</Typography></Stack> : null}
          <Box sx={{ bgcolor: "background.paper", borderRadius: 3, p: { xs: 1.5, sm: 2.5 }, border: "1px solid", borderColor: "divider" }}>{renderStep()}</Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, justifyContent: "space-between", gap: 1 }}>
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: "none" }}>{result ? "Cerrar" : "Cancelar"}</Button>
        <Stack direction="row" spacing={1}>
          {step > 0 && !(step === finalStep && result) ? <Button startIcon={<ArrowBackRoundedIcon />} onClick={goBack} disabled={busy} sx={{ textTransform: "none" }}>Atrás</Button> : null}
          {showFooterNext ? <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} onClick={goForward} disabled={footerNextDisabled} sx={{ textTransform: "none", fontWeight: 900 }}>{nextLabel}</Button> : null}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
